import React, { memo, useMemo } from 'react';
import styled from 'styled-components';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { DashboardQuery } from 'src/graphql/_generated';
import { numeralFormat } from 'src/utils/misc';
import { useStateSelector } from 'src/redux';
import { isEqual } from 'lodash';

const Wrap = styled.div<{ $isPrintable?: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;

  .highcharts-container {
    width: 100% !important;
    height: 100% !important;
  }
`;

export interface ControlChartProps {
  value: DashboardQuery['dashboard']['controlImplementation'];
  isPrintable?: boolean;
  width?: number;
  height?: number;
}

export const ControlChart: React.FC<ControlChartProps> = memo(
  ({ value, isPrintable = false, width = 150, height = 150 }) => {
    const colors = useStateSelector(({ theme }) => theme.colors);

    const chartData = useMemo(
      () => [
        {
          name: 'Not Started',
          y: value?.notStartedCost || 0,
          count: value?.notStartedCount || 0,
          color: colors.prussianBlue,
        },
        {
          name: 'In Progress',
          y: value?.inProgressCost || 0,
          count: value?.inProgressCount || 0,
          color: colors.brightBlue,
        },
        {
          name: 'Live',
          y: value?.liveCost || 0,
          count: value?.liveCount || 0,
          color: colors.gp1,
        },
        {
          name: 'Other',
          y: value?.otherCost || 0,
          count: value?.otherCount || 0,
          color: colors.cflowerBlue,
        },
      ],
      [value, colors],
    );

    const chartOptions = useMemo(
      () => ({
        chart: {
          type: 'pie',
          backgroundColor: 'transparent',
          height: height,
          width: width,
          margin: [0, 0, 0, 0],
          spacing: [0, 0, 0, 0],
          animation: false,
        },
        title: {
          text: '',
        },
        plotOptions: {
          pie: {
            innerSize: '40%',
            dataLabels: {
              enabled: false,
            },
            showInLegend: false,
            borderWidth: 0,
            cursor: isPrintable ? 'default' : 'pointer',
            animation: false,
            states: {
              hover: {
                enabled: !isPrintable,
                brightness: isPrintable ? 0 : 0.1,
                borderWidth: 0,
              },
            },
          },
        },
        series: [
          {
            name: 'Control Implementation',
            data: chartData,
            animation: false,
          },
        ],
        tooltip: {
          enabled: !isPrintable,
          borderRadius: 6,
          backgroundColor: 'transparent',
          borderColor: 'transparent',
          borderWidth: 0,
          shadow: false,
          style: { fontSize: '13px' },
          useHTML: true,
          outside: true,
          // @ts-ignore
          formatter: function () {
            return `<div style="padding:8px 12px; border-radius:6px; color: ${colors.prussianBlue}; background-color:${colors.white};filter: drop-shadow(0px 0px 16px ${colors.shadow})">
               <span style="color: ${colors.cflowerBlue}; font-weight: 600">Status: </span>
                  <b>${
                    // @ts-ignore
                    this.point.name
                  }</b>
                  </br>
                  <span style="color: ${colors.cflowerBlue}; font-weight: 600">Cost: </span>
                  <b>${numeralFormat(
                    // @ts-ignore
                    this.y,
                  )}</b>
                  </br>
                  <span style="color: ${colors.cflowerBlue}; font-weight: 600">Total: </span>
                  <b>${
                    // @ts-ignore
                    this.point.count
                  }</b>
                </div>`;
          },
        },
        credits: {
          enabled: false,
        },
        legend: {
          enabled: false,
        },
        // Disable all animations globally
        animation: false,
      }),
      [chartData, colors, isPrintable, width, height],
    );

    if (!value) return null;

    return (
      <Wrap $isPrintable={isPrintable}>
        <HighchartsReact
          highcharts={Highcharts}
          options={chartOptions}
          allowChartUpdate={true}
          updateArgs={[true, true, true]}
          containerProps={{ style: { width: '100%', height: '100%' } }}
        />
      </Wrap>
    );
  },
  isEqual,
);

export default ControlChart;
